// pages/admin/FeeManagement.jsx
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
// import { feeAPI } from '../../services/feeApi';
// import { adminAPI } from '../../services/api';
import { feeAPI, adminAPI } from '../../services/api';
const FeeManagement = () => {
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [categoriesRes, classesRes, sessionsRes] = await Promise.all([
        feeAPI.getFeeCategories(),
        adminAPI.getClasses(),
        adminAPI.getAcademicSessions(),
      ]);
      setCategories(categoriesRes.data);
      setClasses(classesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchStructures = async () => {
    // Assuming there's an endpoint to list structures; if not, adjust.
    // For now we'll just show a placeholder.
    setStructures([]);
  };

  const handleCreateCategory = async (values, { resetForm, setSubmitting }) => {
    try {
      await feeAPI.createFeeCategory(values);
      toast.success('Fee category created');
      const res = await feeAPI.getFeeCategories();
      setCategories(res.data);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Creation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateStructure = async (values, { resetForm, setSubmitting }) => {
    try {
      await feeAPI.createFeeStructure(values);
      toast.success('Fee structure created');
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Creation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateInvoices = async () => {
    if (!selectedClass || !selectedSession) {
      toast.error('Please select class and session');
      return;
    }
    try {
      const res = await feeAPI.generateInvoices(selectedClass, selectedSession, selectedTerm || null);
      toast.success(`Generated ${res.data.invoices_created} invoices`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invoice generation failed');
    }
  };

  const categorySchema = Yup.object({
    name: Yup.string().required('Required'),
    description: Yup.string(),
    is_optional: Yup.boolean(),
  });

  const structureSchema = Yup.object({
    class_id: Yup.number().required('Required'),
    academic_session_id: Yup.number().required('Required'),
    term_id: Yup.number().nullable(),
    fee_category_id: Yup.number().required('Required'),
    amount: Yup.number().positive().required('Required'),
    due_date: Yup.date().nullable(),
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Fee Management</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['categories', 'structures', 'invoices'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Create Fee Category</h2>
          <Formik
            initialValues={{ name: '', description: '', is_optional: false }}
            validationSchema={categorySchema}
            onSubmit={handleCreateCategory}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <Field name="name" className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <Field as="textarea" name="description" className="input-field" rows="2" />
                </div>
                <div className="flex items-center">
                  <Field type="checkbox" name="is_optional" className="mr-2" />
                  <label className="text-sm text-gray-700">Optional fee</label>
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  Create Category
                </button>
              </Form>
            )}
          </Formik>

          <h2 className="text-lg font-semibold mt-8 mb-4">Existing Categories</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Optional</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((cat) => (
                  <tr key={cat.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{cat.name}</td>
                    <td className="px-6 py-4">{cat.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{cat.is_optional ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Structures Tab */}
      {activeTab === 'structures' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Create Fee Structure</h2>
          <Formik
            initialValues={{
              class_id: '',
              academic_session_id: '',
              term_id: '',
              fee_category_id: '',
              amount: '',
              due_date: '',
            }}
            validationSchema={structureSchema}
            onSubmit={handleCreateStructure}
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Class *</label>
                  <Field as="select" name="class_id" className="input-field">
                    <option value="">Select class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Academic Session *</label>
                  <Field as="select" name="academic_session_id" className="input-field">
                    <option value="">Select session</option>
                    {sessions.map(s => <option key={s.id} value={s.id}>{s.academic_session}</option>)}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Term (optional)</label>
                  <Field as="select" name="term_id" className="input-field">
                    <option value="">All terms</option>
                    {/* Populate terms based on selected session if needed */}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fee Category *</label>
                  <Field as="select" name="fee_category_id" className="input-field">
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount *</label>
                  <Field type="number" step="0.01" name="amount" className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date (optional)</label>
                  <Field type="date" name="due_date" className="input-field" />
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  Create Structure
                </button>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Generate Invoices for Class</h2>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700">Class *</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="input-field"
              >
                <option value="">Select class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Academic Session *</label>
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="input-field"
              >
                <option value="">Select session</option>
                {sessions.map(s => <option key={s.id} value={s.id}>{s.academic_session}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Term (optional)</label>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="input-field"
              >
                <option value="">All terms</option>
                {/* Populate terms dynamically if needed */}
              </select>
            </div>
            <button onClick={handleGenerateInvoices} className="btn-primary">
              Generate Invoices
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeManagement;